import L from "leaflet";
import type { ForteXApi, TrackPoint } from "@fortex/api";
import type { LatLngTuple } from "../core/options";
import { bearingDegrees } from "../geo/geodesy";
import { hostDefinitionOf } from "./host";
import type { LayerSource, SourceFactory, TrackPlayback, TrackPlaybackState } from "./types";

export type TrackVertex = {
  position: LatLngTuple;
  time: number;
  speed: number | null;
  heading: number | null;
};

export const arrowSpacingPixels = 80;

export const trackSpeeds = [1, 5, 20];

const tickMs = 100;

export function trackVertices(points: TrackPoint[]): TrackVertex[] {
  return points
    .filter(a => a.lat !== null && a.lon !== null && Number.isFinite(a.lat) && Number.isFinite(a.lon))
    .map(a => ({ position: [a.lat as number, a.lon as number] as LatLngTuple, time: Date.parse(a.date), speed: a.speed, heading: a.heading }))
    .map(a => ({ ...a, time: Number.isFinite(a.time) ? a.time : 0 }))
    .sort((a, b) => a.time - b.time);
}

export function arrowPlacements(map: L.Map, vertices: TrackVertex[], spacing = arrowSpacingPixels): { position: LatLngTuple; bearing: number }[] {
  const result: { position: LatLngTuple; bearing: number }[] = [];

  if (vertices.length < 2) return result;

  const points = vertices.map(a => map.latLngToContainerPoint(a.position));
  let next = spacing;
  let travelled = 0;

  for (let index = 1; index < points.length; index++) {
    const from = points[index - 1];
    const to = points[index];
    const length = from.distanceTo(to);

    if (length === 0) continue;

    const bearing = bearingDegrees(vertices[index - 1].position, vertices[index].position);

    while (next <= travelled + length) {
      const ratio = (next - travelled) / length;
      const point = L.point(from.x + (to.x - from.x) * ratio, from.y + (to.y - from.y) * ratio);
      const latLng = map.containerPointToLatLng(point);

      result.push({ position: [latLng.lat, latLng.lng], bearing });
      next += spacing;
    }

    travelled += length;
  }

  return result;
}

export function positionAt(vertices: TrackVertex[], time: number): LatLngTuple | null {
  if (vertices.length === 0) return null;
  if (time <= vertices[0].time) return vertices[0].position;

  const last = vertices[vertices.length - 1];

  if (time >= last.time) return last.position;

  for (let index = 1; index < vertices.length; index++) {
    const previous = vertices[index - 1];
    const current = vertices[index];

    if (time > current.time) continue;

    const span = current.time - previous.time;
    const ratio = span <= 0 ? 1 : (time - previous.time) / span;

    return [previous.position[0] + (current.position[0] - previous.position[0]) * ratio, previous.position[1] + (current.position[1] - previous.position[1]) * ratio];
  }

  return last.position;
}

function arrowIcon(bearing: number, color: string): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<span class="fxmap-track-arrow" style="--fxmap-track-color:${color};transform:rotate(${Math.round(bearing)}deg)"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  });
}

export type TrackSource = LayerSource & { load(): Promise<TrackVertex[]>; vertices(): TrackVertex[] };

export function createTrackSource(api: ForteXApi): SourceFactory {
  return (entry, context) => {
    const definition = hostDefinitionOf(entry);
    const track = definition?.track ?? null;

    if (definition === null || track === null) return null;

    const map = context.map;
    const pane = context.paneFor(entry);
    const color = definition.color ?? "#0E6B7A";
    const group = L.layerGroup([], { pane });
    const arrows = L.layerGroup([], { pane }).addTo(group);
    const listeners = new Set<(state: TrackPlaybackState) => void>();
    let vertices: TrackVertex[] = [];
    let line: L.Polyline | null = null;
    let position: L.CircleMarker | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let playing = false;
    let speed = trackSpeeds[0];
    let elapsed = 0;

    const duration = (): number => (vertices.length < 2 ? 0 : vertices[vertices.length - 1].time - vertices[0].time);

    const currentPosition = (): LatLngTuple | null => (vertices.length === 0 ? null : positionAt(vertices, vertices[0].time + elapsed));

    const state = (): TrackPlaybackState => ({ objectId: track.objectId, playing, speed, elapsedMs: elapsed, durationMs: duration(), position: currentPosition(), vertexCount: vertices.length });

    const notify = () => {
      const snapshot = state();

      for (const listener of [...listeners]) listener(snapshot);
    };

    const placeArrows = (): void => {
      arrows.clearLayers();

      if (!map.hasLayer(group)) return;

      for (const placement of arrowPlacements(map, vertices)) {
        L.marker(placement.position, { icon: arrowIcon(placement.bearing, color), interactive: false, keyboard: false, pane }).addTo(arrows);
      }
    };

    const movePosition = (): void => {
      const at = currentPosition();

      if (position !== null && at !== null) position.setLatLng(at);

      notify();
    };

    const pause = (): void => {
      playing = false;

      if (timer !== null) clearInterval(timer);

      timer = null;
      notify();
    };

    const play = (): void => {
      if (vertices.length < 2) return;
      if (elapsed >= duration()) elapsed = 0;

      playing = true;
      timer = setInterval(() => {
        elapsed = Math.min(duration(), elapsed + tickMs * speed);
        movePosition();

        if (elapsed >= duration()) pause();
      }, tickMs);
      notify();
    };

    const render = (): void => {
      group.clearLayers();
      arrows.clearLayers();
      group.addLayer(arrows);
      line = null;
      position = null;

      if (vertices.length === 0) return;

      line = L.polyline(
        vertices.map(a => a.position),
        { color, weight: 3, opacity: 0.9, interactive: false, pane, className: "fxmap-track-line" }
      ).addTo(group);

      if (track.playback) {
        position = L.circleMarker(vertices[0].position, { radius: 7, color: "#ffffff", weight: 2, fillColor: color, fillOpacity: 1, interactive: false, pane, className: "fxmap-track-position" }).addTo(group);
      }

      placeArrows();
    };

    const load = async (): Promise<TrackVertex[]> => {
      const points = await api.tracks(track.objectId, track.from ?? null, track.to ?? null);

      vertices = trackVertices(points ?? []);
      elapsed = 0;
      pause();
      render();

      return vertices;
    };

    const playback: TrackPlayback = {
      state,
      play,
      pause,
      seek: value => {
        elapsed = Math.max(0, Math.min(duration(), value));
        movePosition();
      },
      setSpeed: value => {
        speed = value > 0 ? value : trackSpeeds[0];
        notify();
      },
      subscribe: listener => {
        listeners.add(listener);

        return () => {
          listeners.delete(listener);
        };
      }
    };

    map.on("zoomend", placeArrows);
    group.on("add", placeArrows);

    const source: TrackSource = {
      entry,
      kind: "track",
      layer: group,
      pane,
      bounds: () => (line === null ? null : line.getBounds().isValid() ? line.getBounds() : null),
      features: null,
      info: null,
      legend: () => entry.legend,
      playback: track.playback ? playback : null,
      load,
      vertices: () => vertices,
      dispose: () => {
        pause();
        listeners.clear();
        map.off("zoomend", placeArrows);
        group.off("add", placeArrows);
        group.clearLayers();
        group.remove();
      }
    };

    return source;
  };
}
